import { Controller } from "@hotwired/stimulus"
import * as echarts from "echarts/core"
import { LineChart } from "echarts/charts"
import { GridComponent, TooltipComponent, LegendComponent, MarkLineComponent } from "echarts/components"
import { CanvasRenderer } from "echarts/renderers"
import { ABSENT_VALUE, isAbsent } from "../../helpers/chart_values"

echarts.use([LineChart, GridComponent, TooltipComponent, LegendComponent, MarkLineComponent, CanvasRenderer])

// Renders the reporting time-series chart with echarts. The metric tabs swap the
// active series client-side (all series are embedded), so switching is instant and
// needs no server round-trip. Resizes with its container for mobile.
export default class extends Controller {
  static targets = ["canvas", "tab", "actionSelect", "actionLabel"]
  static values = {
    categories: Array,
    series: Array,
    active: { type: Number, default: 0 },
    // Optional dashed reference lines [{ value, color }]; empty for reporting charts.
    thresholds: { type: Array, default: [] },
    comparisonLabels: { type: Array, default: [] },
    // Hues for the 2nd/3rd prior-period lines (the 1st reuses its metric's own hue, dashed),
    // passed in from V2::ChartColors so brand colors live only in Ruby.
    comparisonPalette: { type: Array, default: [] },
    // Conversion selector (PRD-208): option labels, index 0 being the blended total, and
    // { label: { metricKey: { data, comparison_data } } }. Both empty on charts without one.
    actions: { type: Array, default: [] },
    byAction: { type: Object, default: {} },
    activeAction: { type: Number, default: 0 },
  }

  connect() {
    this.chart = echarts.init(this.canvasTarget)
    // Re-derive the active tab from activeValue so the highlight matches the plotted
    // series. After a Turbo cache restore the cached DOM may still mark a different tab
    // active while renderChart() draws activeValue's series — sync them here.
    this.tabTargets.forEach((tab, i) => tab.classList.toggle("active", i === this.activeValue))
    this.toggleActionSelect()
    this.renderChart()
    this.resizeObserver = new ResizeObserver(() => this.chart.resize())
    this.resizeObserver.observe(this.canvasTarget)
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this.chart?.dispose()
  }

  select(event) {
    const index = Number(event.currentTarget.dataset.index)
    if (index === this.activeValue) return
    this.activeValue = index
    this.tabTargets.forEach((tab, i) => tab.classList.toggle("active", i === index))
    this.toggleActionSelect()
    this.renderChart()
  }

  selectAction(event) {
    this.activeActionValue = Number(event.currentTarget.value)
    if (this.hasActionLabelTarget) this.actionLabelTarget.textContent = event.currentTarget.textContent.trim()
    this.closeActionSelect()
    this.renderChart()
  }

  // The dropdown is a <details>, which only closes via its own summary — so it has to be closed on
  // pick and on an outside click, like every other menu in the app.
  closeActionSelect() {
    if (!this.hasActionSelectTarget) return

    this.actionSelectTarget.removeAttribute("open")
    // Reopening pre-filtered would hide options the reader never excluded.
    this.application
      .getControllerForElementAndIdentifier(this.actionSelectTarget, "list-filter")
      ?.clear()
  }

  closeActionSelectOnOutsideClick(event) {
    if (!this.hasActionSelectTarget || !this.actionSelectTarget.open) return
    if (this.actionSelectTarget.contains(event.target)) return

    this.closeActionSelect()
  }

  closeActionSelectOnEscape(event) {
    if (event.key === "Escape") this.closeActionSelect()
  }

  // The selector only applies to metrics that are attributable to a single conversion, so hide it on
  // the others (Spend, and every metric on the spend-side-only reports).
  toggleActionSelect() {
    if (!this.hasActionSelectTarget) return
    this.actionSelectTarget.classList.toggle("hidden", !this.perActionMetric())
  }

  // Whether the active metric has per-conversion series available (Spend never is, so the selector
  // hides on that tab).
  perActionMetric() {
    const metric = this.seriesValue[this.activeValue]
    if (!metric) return false
    return Object.values(this.byActionValue).some((metrics) => metrics[metric.key])
  }

  // The plotted series for the active metric: the picked option's own series when it has one for this
  // metric, else the blended account-wide series. The account-total option deliberately has no entry
  // in byActionValue, so it falls through here — no index is special-cased, which is what lets the
  // combined "Selected conversions" series sit at index 0 and be the default.
  activeSeries() {
    const metric = this.seriesValue[this.activeValue]
    if (!metric) return metric
    const label = this.actionsValue[this.activeActionValue]
    const scoped = (this.byActionValue[label] || {})[metric.key]
    return scoped ? { ...metric, ...scoped } : metric
  }

  renderChart() {
    const metric = this.activeSeries()
    if (!metric) return

    const format = (value) => this.format(value, metric.format)

    const series = [{
      type: "line",
      name: metric.label,
      data: metric.data,
      smooth: true,
      showSymbol: false,
      // A null (a CPA whose conversion count displays as zero) leaves a gap rather than
      // dropping the line to zero, which would read as a real $0 cost.
      connectNulls: false,
      lineStyle: { color: metric.color, width: 2 },
      itemStyle: { color: metric.color },
      areaStyle: { color: this.rgba(metric.color, 0.1) },
      markLine: this.markLine(format),
    }]

    // Comparison windows (PRD-130): one dashed line each, aligned by day-index to the
    // current series (year-over-year can plot several prior years). The 1st reuses the
    // metric's hue (matching the single-comparison look); later ones get distinct hues.
    // No area fill so the lines don't muddy each other.
    const comparisonWindows = metric.comparison_data || []
    comparisonWindows.forEach((data, w) => {
      const color = w === 0 ? metric.color : (this.comparisonPaletteValue[w - 1] || metric.color)
      series.push({
        type: "line",
        name: this.comparisonLabelsValue[w] || (w === 0 ? "Previous period" : `${w + 1} years ago`),
        data,
        smooth: true,
        showSymbol: false,
        lineStyle: { color, width: 2, type: "dashed", opacity: w === 0 ? 0.55 : 0.85 },
        itemStyle: { color },
      })
    })

    // A legend only when there's more than one comparison line (single-comparison charts
    // stay legend-free, visually unchanged); make room at the top when it's shown.
    const showLegend = comparisonWindows.length > 1

    this.chart.setOption({
      legend: showLegend ? { top: 0, icon: "roundRect", textStyle: { color: "#55615F", fontFamily: "Aeonik Fono, monospace", fontSize: 11 } } : undefined,
      grid: { left: 4, right: 16, top: showLegend ? 32 : 16, bottom: 4, containLabel: true },
      tooltip: {
        trigger: "axis",
        backgroundColor: "#2C3131",
        borderWidth: 0,
        textStyle: { color: "#F1F3F2", fontFamily: "Aeonik Fono, monospace" },
        valueFormatter: format,
      },
      xAxis: {
        type: "category",
        data: this.categoriesValue,
        boundaryGap: false,
        axisLine: { lineStyle: { color: "#E8EDEB" } },
        axisTick: { show: false },
        axisLabel: { color: "#9CA6A4", fontFamily: "Aeonik Fono, monospace", fontSize: 11 },
      },
      yAxis: {
        type: "value",
        splitLine: { lineStyle: { color: "#F1F3F2" } },
        axisLabel: { color: "#9CA6A4", fontFamily: "Aeonik Fono, monospace", fontSize: 11, formatter: (v) => this.compact(v, metric.format) },
      },
      series,
    }, true)
  }

  // Dashed horizontal reference lines at each threshold, labeled with the value.
  markLine(format) {
    if (!this.thresholdsValue.length) return undefined
    return {
      silent: true,
      symbol: "none",
      data: this.thresholdsValue.map((t) => ({
        yAxis: t.value,
        lineStyle: { color: t.color, type: "dashed", width: 1 },
        label: {
          formatter: () => format(t.value),
          color: t.color,
          fontFamily: "Aeonik Fono, monospace",
          fontSize: 10,
          position: "insideEndTop",
        },
      })),
    }
  }

  // Full value for tooltips (and the markLine labels). See helpers/chart_values for why an absent
  // value can't be detected with a null check alone.
  format(value, type) {
    if (isAbsent(value)) return ABSENT_VALUE

    const n = Number(value)
    if (type === "currency") return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    if (type === "percent") return `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`
    return n.toLocaleString(undefined, { maximumFractionDigits: 1 })
  }

  // Abbreviated value for axis ticks (e.g. $1.2k, 3.4M). Guarded like #format: ECharts derives
  // ticks from the data extent, so a series that is absent end to end must not label an axis "NaN".
  compact(value, type) {
    if (isAbsent(value)) return ABSENT_VALUE

    const n = Number(value)
    const abs = Math.abs(n)
    let label = n.toLocaleString(undefined, { maximumFractionDigits: 1 })
    if (abs >= 1_000_000) label = `${(n / 1_000_000).toFixed(1)}M`
    else if (abs >= 1_000) label = `${(n / 1_000).toFixed(1)}k`
    if (type === "currency") return `$${label}`
    if (type === "percent") return `${label}%`
    return label
  }

  rgba(hex, alpha) {
    const value = hex.replace("#", "")
    // Only #rrggbb is supported; anything else (3-digit, named) would parse to NaN, so
    // fall back to the opaque color rather than emit rgba(NaN, …).
    if (value.length !== 6) return hex
    const r = parseInt(value.substring(0, 2), 16)
    const g = parseInt(value.substring(2, 4), 16)
    const b = parseInt(value.substring(4, 6), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }
}
